/**
 * 個別単価＝**単価3層の第3層**（FR-14・04 §5・T-D8）。C-09 詳細と C-10 の両方から使う。
 *
 * 層（強い順）：③個別単価 → ②得意先の掛率×定価 → ①定価。ここで設定した値は掛率より優先される。
 *
 * ★ **画面で「掛率適用後の価格」を計算して見せない**。`backend/app/domains/masters/pricing.py`
 *   が「カタログに出す値」と「注文に保存する値」を同じ関数から出すことで金額のズレを防いでいる
 *   （同ファイル冒頭）。ここで JS の浮動小数で3つ目の実装を作ると、その保証を壊す側になる。
 *   出すのは**定価**と**個別単価**（＝どちらも DB にある事実）だけにする。
 *
 * ★ 一覧は**個別単価が付いている商品を1件ずつ引く**。商品一覧 API の `limit` は 100 なので、
 *   商品が 100 を超える卸で「1ページ目に無い商品の個別単価が画面から消える」を作らない。
 *
 * 認可（03 §7）：**設定・変更は staff も可・削除は admin のみ**（backend の PUT=ConsoleUser /
 * DELETE=ConsoleAdmin にそのまま合わせる＝押しても 403 になるだけのボタンを出さない）。
 */

import { console_, type Customer, type Product } from "@kenzai/api-client";
import { Badge, Card, ConfirmDialog, ErrorNote, Money, Spinner, Table, Td } from "@kenzai/ui";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { moneyOk } from "./masterForms";
import { hintStyle, inputStyle } from "./formStyles";

export function CustomerPrices({ customer, isAdmin }: { customer: Customer; isAdmin: boolean }) {
  const qc = useQueryClient();
  const customerId = customer.id;
  const [q, setQ] = useState("");
  const [removing, setRemoving] = useState<{ productId: number; name: string } | null>(null);

  const prices = useQuery({
    queryKey: ["customer-prices", customerId],
    queryFn: () => console_.customerPrices(customerId),
  });
  const rows = prices.data ?? [];

  const products = useQueries({
    queries: rows.map((r) => ({
      queryKey: ["product", r.product_id],
      queryFn: () => console_.product(r.product_id),
      staleTime: 60_000,
    })),
  });

  const picker = useQuery({
    queryKey: ["products", "price-picker", q],
    queryFn: () => console_.products(`?limit=50${q ? `&q=${encodeURIComponent(q)}` : ""}`),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["customer-prices", customerId] });

  const put = useMutation({
    mutationFn: (v: { product_id: number; unit_price: string }) => console_.putCustomerPrice(customerId, v),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (productId: number) => console_.deleteCustomerPrice(customerId, productId),
    onSettled: () => {
      setRemoving(null);
      refresh();
    },
  });

  const overrideOf = new Map(rows.map((r) => [r.product_id, r.unit_price]));

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-4)" }}>
      {(put.isError || remove.isError || prices.isError) && (
        <ErrorNote error={put.error ?? remove.error ?? prices.error} />
      )}

      <div style={hintStyle}>
        個別単価は<strong>掛率より優先</strong>されます（この取引先の掛率：
        <span className="kz-num">{customer.kake_rate ?? "未設定"}</span>）。設定していない商品は
        掛率、掛率も無ければ定価で計算されます。
      </div>

      <div>
        <h3 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>
          設定されている個別単価
        </h3>
        {prices.isLoading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <div style={hintStyle} data-testid="no-customer-prices">
            まだありません。下の一覧から商品を探して設定してください。
          </div>
        ) : (
          <Card padding="0">
            <Table head={["品番", "商品", "定価", "個別単価", "操作"]}>
              {rows.map((r, i) => {
                const p = products[i]?.data;
                return (
                  <tr key={r.product_id} data-testid={`price-row-${r.product_id}`}>
                    <Td>
                      <span className="kz-num">{p?.display_no ?? "—"}</span>
                    </Td>
                    <Td style={{ fontWeight: 600 }}>
                      {/* 商品が引けない＝廃番でも消えないので「取得中」と「不明」を区別して出す */}
                      {p ? p.name : products[i]?.isLoading ? "読み込み中…" : `商品 #${r.product_id}`}
                      {p?.status === "disabled" && (
                        <>
                          {" "}
                          <Badge tone="warn">廃番</Badge>
                        </>
                      )}
                    </Td>
                    <Td style={{ whiteSpace: "nowrap" }}>
                      <Money value={p?.list_price ?? null} />
                    </Td>
                    <Td>
                      <PriceInput
                        testid={`price-input-${r.product_id}`}
                        value={r.unit_price}
                        busy={put.isPending}
                        onCommit={(unit_price) => put.mutate({ product_id: r.product_id, unit_price })}
                      />
                    </Td>
                    <Td>
                      {isAdmin && (
                        <button
                          type="button"
                          data-testid={`price-remove-${r.product_id}`}
                          onClick={() =>
                            setRemoving({ productId: r.product_id, name: p?.name ?? `商品 #${r.product_id}` })
                          }
                          className="kz-btn kz-btn--sm"
                        >
                          個別単価をやめる
                        </button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </Table>
          </Card>
        )}
      </div>

      <div>
        <h3 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>
          商品を探して設定する
        </h3>
        <input
          type="search"
          data-testid="price-product-search"
          placeholder="商品名・品番で検索"
          onKeyDown={(e) => e.key === "Enter" && setQ((e.target as HTMLInputElement).value)}
          style={{ ...inputStyle, width: 240 }}
        />
        {picker.isLoading ? (
          <Spinner />
        ) : picker.isError ? (
          <ErrorNote error={picker.error} />
        ) : (
          <Card padding="0" style={{ marginTop: "var(--kz-space-2)" }}>
            <Table head={["品番", "商品", "定価", "個別単価"]}>
              {(picker.data?.items ?? []).map((p: Product) => (
                <tr key={p.id} data-testid={`price-pick-${p.id}`}>
                  <Td>
                    <span className="kz-num">{p.display_no}</span>
                  </Td>
                  <Td style={{ fontWeight: 600 }}>{p.name}</Td>
                  <Td style={{ whiteSpace: "nowrap" }}>
                    <Money value={p.list_price} />
                  </Td>
                  <Td>
                    <PriceInput
                      testid={`price-set-${p.id}`}
                      value={overrideOf.get(p.id) ?? ""}
                      busy={put.isPending}
                      onCommit={(unit_price) => put.mutate({ product_id: p.id, unit_price })}
                    />
                  </Td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
      </div>

      {removing && (
        <ConfirmDialog
          title={`${removing.name} の個別単価をやめますか？`}
          body="この取引先の単価は、掛率（無ければ定価）での計算に戻ります。すでに入っている注文の金額は変わりません。"
          // ConfirmDialog の取消ボタンが「やめる」なので、確定側を「やめる」にすると
          // **同じ文言のボタンが2つ並ぶ**（どちらが実行か読めない）＝目的語を付ける
          confirmLabel="個別単価をやめる"
          tone="danger"
          busy={remove.isPending}
          onConfirm={() => remove.mutate(removing.productId)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

/**
 * 個別単価の入力＝**Enter か blur で確定**（1文字打つたびに API を叩かない）。
 *
 * 金額は NUMERIC(12,0)＝円の整数（04 §1 #5）。小数を通すと PostgreSQL が黙って丸めるので、
 * ここで弾いて理由を出す。**空欄にしても消えない**（消すのは「個別単価をやめる」＝DELETE）。
 */
function PriceInput({
  testid,
  value,
  busy,
  onCommit,
}: {
  testid: string;
  value: string;
  busy: boolean;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [touched, setTouched] = useState(false);
  // 外から値が変わったら（保存後の再取得・別行の更新）追従する
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(value);
    setTouched(false);
  }

  const ok = moneyOk(draft);
  const commit = () => {
    const next = draft.trim();
    if (next === value.trim() || !ok) return;
    onCommit(next);
  };

  return (
    <div style={{ display: "grid", gap: 2 }}>
      <input
        data-testid={testid}
        value={draft}
        disabled={busy}
        inputMode="decimal"
        placeholder="—"
        onChange={(e) => {
          setDraft(e.target.value);
          setTouched(true);
        }}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        style={{ ...inputStyle, width: 120, minHeight: 36 }}
      />
      {touched && draft.trim() !== "" && !ok && (
        <span style={{ ...hintStyle, color: "var(--kz-status-danger)" }}>
          0 以上・12桁までの円・整数で入力してください
        </span>
      )}
      {/* 空欄にしても消えない＝黙って何も起きないと「消したつもり」で放置される */}
      {touched && draft.trim() === "" && value.trim() !== "" && (
        <span style={hintStyle}>空にしても消えません。解除は「個別単価をやめる」から。</span>
      )}
    </div>
  );
}
